import { api, React } from '@/app'
import { onUpdate, useI18N, useState } from '@codeleap/common'
import { queryClient } from '../queryClient'
import { Achievement, AchievementLevel, Chapter, Module, Page } from '@/types'
import { useFlatlistProps } from '@/utils'
import { modulesManager } from './modules'
import { isLoggedIn, QueryKeys, useAuthState } from '../session'
import { achievementsOperations } from '../achievements'
import { APIClient } from '@/services'
import { PaginationResponse, QueryManager } from '@/packages'
import { achievementsStore } from '@/stores'

const BASE_URL = 'learn/chapters/'

export const readPage = async (page: Page['id']) => {
  if (!isLoggedIn() || ['start', 'end'].includes(page as string)) return
  try {
    const response = await api.get(BASE_URL + 'read_page/', {
      params: {
        page,
      },
    })

    APIClient.Community.refreshFeed()
    return response.data
  } catch (error) {
    console.error('Error reading page:', error)
  }
}

export const chaptersManager = new QueryManager({
  itemType: {} as Chapter,
  name: 'chapters',
  queryClient: queryClient.client,

  listItems: async (limit, offset, filters) => {
    const response = await api.get<PaginationResponse<Chapter>>(BASE_URL, {
      params: {
        limit,
        offset,
        ...filters,
      },
    })

    return response.data
  },
  retrieveItem: async (id) => {
    const response = await api.get<Chapter>(`${BASE_URL}${id}/`)

    return response.data
  },
})

type UseChapters = {
  moduleId: Module['id']
}

export const useChapters = ({ moduleId }: UseChapters) => {
  const { t } = useI18N()

  const module = modulesManager.useItem({
    id: moduleId,
  })

  const chapters = chaptersManager.use({
    filter: {
      module: moduleId,
    },
  })

  const { isLoggedIn } = useAuthState()

  onUpdate(() => {
    chapters?.refresh()
    module?.refresh()
  }, [isLoggedIn])

  const data: Chapter[] = React.useMemo(() => {
    if (!chapters?.items) return []
    return [...chapters.items]
      .map((i, index) => ({ ...i, __index: index }))
      .sort((a, b) => (a.done === b.done ? a.__index - b.__index : a.done ? 1 : -1))
  }, [chapters?.items])

  const listProps = useFlatlistProps(chapters, {
    noMoreItemsText: t('learn.noMoreLessons'),
    forceLoading: module?.query?.isFetching,
  })

  return {
    chaptersListProps: {
      ...listProps,
      data,
    },
    chapters,
    data,
    moduleQuery: module,
    module: module?.data,
  }
}

export const verifyCollectionAchievementProgress = async (achievements: Achievement[]) => {
  if (!isLoggedIn()) return { progressed: false, data: null }
  const metric = 'collections_completed'

  const collectionAchievement = achievements?.find(a => a?.progression_metric == metric)
  const data = await achievementsOperations.mutations.getProgress(metric)

  const currentLevel = collectionAchievement?.levels?.list[collectionAchievement?.levels?.current_level_index]
  const comparedLevel = data?.achievement_progress?.[0]

  const progressed = comparedLevel?.id !== currentLevel?.id

  if (progressed) {
    achievementsStore.verifyProgress(data?.achievement_progress, false)
  }

  return {
    progressed,
    data: data?.achievement_progress,
  }
}
